import { Checkbox, ListItem, ListItemText } from "@mui/material";
import React, { FC } from "react";
import { MultiOptionsDataType } from "../MuiMultiSelect";

interface ListItemWithCheckboxProps {
  selectedItems: Array<string | number>;
  disabled?: boolean;
  showDoubleLabel?: boolean;
  focusedIndex: number;
  handleToggleItem: (option: MultiOptionsDataType) => void;
  itemRefs: React.MutableRefObject<(HTMLLIElement | null)[]>;
  showSelectAllOption?: boolean;
  showDeclineAllOption?: boolean;
  searchText?: string;
  filteredOptions?: MultiOptionsDataType[];
  selectAllPlaceHolder?: string;
  declineAllPlaceHolder?: string;
  handleSelectAll?: () => void;
  handleDeclineAll?: () => void;
  checkedStateARIALabel?: string;
  unCheckedStateARIALabel?: string;
  declinedAll?: boolean;
}

const getSelectAllState = (
  selectedItems: Array<string | number>,
  filteredOptions: MultiOptionsDataType[],
) => ({
  checked:
    filteredOptions.length > 0 &&
    filteredOptions.length === selectedItems.length,
  indeterminate:
    selectedItems.length > 0 && selectedItems.length < filteredOptions.length,
});

const ListItemWithCheckbox: FC<ListItemWithCheckboxProps> = ({
  selectedItems,
  disabled = false,
  showDoubleLabel = false,
  focusedIndex,
  handleToggleItem,
  itemRefs,
  showSelectAllOption = false,
  showDeclineAllOption = false,
  searchText = "",
  filteredOptions = [],
  selectAllPlaceHolder = "Select All",
  declineAllPlaceHolder = "Decline [None of the below]",
  handleSelectAll = () => {},
  handleDeclineAll = () => {},
  checkedStateARIALabel,
  unCheckedStateARIALabel,
  declinedAll = false,
}) => {
  const selectAllState = getSelectAllState(selectedItems, filteredOptions);

  return (
    <>
      {showDeclineAllOption && !searchText && (
        <ListItem
          tabIndex={0}
          key="all"
          component="li"
          className={`option-item${showDoubleLabel ? " double-label" : ""} header-option-item`}
          onClick={handleDeclineAll}
        >
          <Checkbox
            checked={declinedAll}
            tabIndex={-1}
            disableRipple
            inputProps={{
              "aria-label": `${declineAllPlaceHolder}`,
              "aria-checked": declinedAll,
            }}
          />
          <ListItemText primary={declineAllPlaceHolder} />
        </ListItem>
      )}
      {showSelectAllOption && !searchText && (
        <ListItem
          tabIndex={0}
          key="all"
          component="li"
          className={`option-item${showDoubleLabel ? " double-label" : ""} header-option-item`}
          onClick={declinedAll ? undefined : handleSelectAll}
        >
          <Checkbox
            checked={selectAllState.checked}
            indeterminate={selectAllState.indeterminate}
            disabled={disabled || declinedAll}
            tabIndex={-1}
            disableRipple
            inputProps={{
              "aria-label": `${selectAllPlaceHolder}`,
              "aria-checked": selectAllState.checked,
            }}
          />
          <ListItemText primary={selectAllPlaceHolder} />
        </ListItem>
      )}
      {filteredOptions.map((option, index) => (
        <ListItem
          key={option.value}
          component="li"
          className={`option-item${showDoubleLabel ? " double-label" : ""}`}
          onClick={() => !disabled && !declinedAll && handleToggleItem(option)}
          ref={(el) => (itemRefs.current[index] = el)}
          tabIndex={index === focusedIndex ? 0 : -1}
        >
          <Checkbox
            checked={selectedItems.includes(option.value)}
            disabled={disabled || declinedAll}
            disableRipple
            inputProps={{
              "aria-label": `${option.label} ${selectedItems.includes(option.value) ? checkedStateARIALabel : unCheckedStateARIALabel}`,
              "aria-checked": selectedItems.includes(option.value),
              "aria-disabled": disabled || declinedAll,
            }}
          />
          <ListItemText
            primary={option.label}
            secondary={showDoubleLabel ? option.additionalInfo : undefined}
            className={
              showDoubleLabel ? "checkbox-with-double-label" : undefined
            }
          />
        </ListItem>
      ))}
    </>
  );
};

export default ListItemWithCheckbox;
